// src/components/hooks/useFetchPrograms.js  

import { useState, useEffect } from "react";
import { getDocs, collection } from "firebase/firestore";
import { db } from "../Firebase/firebaseConfig";

/**
 * Custom hook to fetch diplomas and courses from Firestore.
 * Handles loading states, error states, and ensures safe state updates.
 */
const useFetchPrograms = () => {
  const [diplomas, setDiplomas] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loadingDiplomas, setLoadingDiplomas] = useState(true);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [errorDiplomas, setErrorDiplomas] = useState(null);
  const [errorCourses, setErrorCourses] = useState(null);

  useEffect(() => {
    let isMounted = true; // Flag to track if the component is mounted

    /**
     * Fetch diplomas from Firestore.
     */
    const fetchDiplomas = async () => {
      try {
        const diplomasRef = collection(db, "diplomas");
        const diplomasSnapshot = await getDocs(diplomasRef);
        const diplomasData = diplomasSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          semesters: doc.data().semesters || [], // Ensure semesters is an array
          courses: doc.data().courses || [], // Ensure courses is an array
          duration: doc.data().duration || "N/A", // Default duration
          imageURL: doc.data().imageURL || '', // Default imageURL
        }));
        if (isMounted) {
          setDiplomas(diplomasData);
        }
      } catch (error) {
        console.error("Error fetching diplomas:", error);
        if (isMounted) {
          setErrorDiplomas("Failed to load diplomas. Please try again later.");
        }
      } finally {
        if (isMounted) {
          setLoadingDiplomas(false);
        }
      }
    };

    /**
     * Fetch courses from Firestore.
     */
    const fetchCourses = async () => {
      try {
        const coursesRef = collection(db, "courses");
        const coursesSnapshot = await getDocs(coursesRef);
        const coursesData = coursesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          duration: doc.data().duration || "N/A",
          instructorName: doc.data().instructorName || "Unknown Instructor",
          instructorPhotoURL: doc.data().instructorPhotoURL || '',
          imageURL: doc.data().imageURL || '',
        }));
        if (isMounted) {
          setCourses(coursesData);
        }
      } catch (error) {
        console.error("Error fetching courses:", error);
        if (isMounted) {
          setErrorCourses("Failed to load courses. Please try again later.");
        }
      } finally {
        if (isMounted) {
          setLoadingCourses(false);
        }
      }
    };

    // Initiate both fetches in parallel
    fetchDiplomas();
    fetchCourses();

    // Cleanup function to set isMounted to false when component unmounts
    return () => {
      isMounted = false;
    };
  }, []);

  return {
    diplomas,
    courses,
    loadingDiplomas,
    loadingCourses,
    errorDiplomas,
    errorCourses,
  };
};

export default useFetchPrograms;
